import { Component, Input, OnDestroy, OnInit, ViewChild, ElementRef, signal, effect, ChangeDetectionStrategy } from '@angular/core';
import { Store } from '@ngneat/elf';
import { MenuItem, PrimeIcons, SelectItem } from 'primeng/api';
import { ContextMenu } from 'primeng/contextmenu';
import { Subject, forkJoin, catchError, map } from 'rxjs';
import { takeUntil, debounceTime } from 'rxjs/operators';
import { SessionStorageKeys, StoreService } from 'src/app/core/services/store.service';
import { PeriforOnChangeMessages, SignalRService } from 'src/app/shared/services/signal-r.service';
import { ExpansionJointFocus, Packer, PackerSchematicDisplayMode, Packers } from 'src/app/wellbore-inputs/models/packers.model';
import { PackerService } from 'src/app/wellbore-inputs/services/packers.service';
import { LoadCaseService } from '../../services/load-case.service';
import { LoadCaseSummaryResult } from '../../models/load-case.model';
import { PackerSchematicViewerUi } from './models/packer-schematic-viewer.model';
import { lastValueFrom } from 'rxjs';
import { CurrentAppIdsDto } from 'src/app/core/models/current-app-ids-dto';
import { MediatorService } from 'src/app/shared/services/mediator.service';
import { GridItemResizedMessage } from 'src/app/shared/models/mediator-messages.model';
import * as ViewBoxHelpers from 'src/app/wellbore-inputs/components/well-schematic/helpers/viewbox.helper';
import * as PackerSchematicHelpers from 'src/app/wellbore-inputs/components/packers/packer-schematic/helpers/packer-schematic.helper';
import { AppNotificationService } from 'src/app/shared/services/app-notification.service';
import { copySchematicToClipboard } from 'src/app/shared/helpers/schematic-export.helper';

@Component({
  selector: 'app-packer-schematic-viewer',
  templateUrl: './packer-schematic-viewer.component.html',
  styleUrls: ['./packer-schematic-viewer.component.scss'],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class PackerSchematicViewerComponent implements OnInit, OnDestroy {
  private _destroy$ = new Subject<void>();
  private _tubularId!: string;

  // Layout constants
  private readonly CONTAINER_MARGIN = 40; // 20px on each side
  private readonly TOOLBAR_HEIGHT = 300; // Toolbar and bottom margin
  private readonly MAX_SCHEMATIC_WIDTH = 650; // Packer schematics are vertical, cap width
  private readonly MIN_DIMENSION = 100;
  private readonly SVG_CONTENT_PADDING = 20;

  // Component-local reactive state (signals)
  public isLoading = signal(false);
  public errorMessage = signal<string | null>(null);
  public svgContent = signal('');
  public packers = signal<SelectItem[]>([]);
  public loadCases = signal<SelectItem[]>([]);
  public showExpansionJointFocusToggle = signal(false);
  public displayModes: SelectItem[] = [
    { label: 'Tubing to Packer Forces', value: PackerSchematicDisplayMode.TubingToPackerForces },
    { label: 'Latching Force', value: PackerSchematicDisplayMode.LatchingForce },
    { label: 'Packer to Casing Force', value: PackerSchematicDisplayMode.PackerToCasingForce },
    { label: 'Cross Sectional Areas', value: PackerSchematicDisplayMode.CrossSectionalAreas }
  ];
  public expansionJointFocusOptions: SelectItem[] = [
    { label: 'Exp. Joint', value: ExpansionJointFocus.ExpansionJoint },
    { label: 'Packer Body', value: ExpansionJointFocus.PackerBody }
  ];
  private packerModels: Packer[] = [];

  // SVG pan/zoom state (non-reactive, performance-critical)
  public isPanning = signal(false);
  private svgElement!: SVGSVGElement;
  private viewBox: ViewBoxHelpers.ViewBox = { x: 0, y: 0, width: 1000, height: 1000 };
  private originalViewBox: ViewBoxHelpers.ViewBox = { x: 0, y: 0, width: 1000, height: 1000 };
  private scale = 1;
  private panStart: ViewBoxHelpers.Point = { x: 0, y: 0 };

  @ViewChild('schematicContainer', { static: true })
  private schematicContainer!: ElementRef;

  @ViewChild('packerViewerCm', { static: false })
  public packerViewerCm!: ContextMenu;

  // State Management
  private _componentId!: string;
  @Input() set componentId(value: string) {
    this._componentId = value;
    this.store = this._storeService.createStore(this.componentId, new PackerSchematicViewerUi());
    this.selectedPackerIndex.set(this.store.state.selectedPackerIndex);
    this.selectedLoadCase.set(this.store.state.selectedLoadCase);
    this.selectedMode.set(this.store.state.selectedMode || PackerSchematicDisplayMode.TubingToPackerForces);
    this.selectedExpansionJointFocus.set(this.store.state.selectedExpansionJointFocus ?? ExpansionJointFocus.ExpansionJoint);
  }
  get componentId(): string {
    return this._componentId;
  }
  public store!: Store;

  // Form-bound state (signals for OnPush reactivity)
  public selectedPackerIndex = signal<number | undefined>(undefined);
  public selectedLoadCase = signal<number | undefined>(undefined);
  public selectedMode = signal<PackerSchematicDisplayMode>(PackerSchematicDisplayMode.TubingToPackerForces);
  public selectedExpansionJointFocus = signal<ExpansionJointFocus>(ExpansionJointFocus.ExpansionJoint);

  public readonly packerViewerContextMenuItems = signal<MenuItem[]>([
    {
      label: 'Copy schematic as image',
      icon: PrimeIcons.COPY,
      command: () => void this.copySchematicAsImage(),
    }
  ]);

  constructor(
    private _storeService: StoreService,
    private _signalRService: SignalRService,
    private _packerService: PackerService,
    private _loadCaseService: LoadCaseService,
    private _messenger: MediatorService,
    private _elementRef: ElementRef,
    private _toaster: AppNotificationService
  ) {
    // Watch svgContent signal and attach listeners when it changes
    effect(() => {
      const svg = this.svgContent();
      if (svg) {
        queueMicrotask(() => this.attachSvgEventListeners());
      }
    });
  }

  async ngOnInit(): Promise<void> {
    const hub = this._signalRService.getConnectionToNotificationHub();
    this._signalRService.subscribeToEventFilteredByDesignId(
      hub,
      SignalRService.ON_PFB_CHANGE,
      data => this.handleSignalR(data)
    );

    // Subscribe to grid resize events
    this._messenger.of(GridItemResizedMessage)
      .pipe(debounceTime(200), takeUntil(this._destroy$))
      .subscribe(() => {
        if (this.selectedPackerIndex() !== undefined && this.selectedLoadCase() !== undefined) {
          this.loadSchematic();
        }
      });

    this._storeService.watchSession<CurrentAppIdsDto>(SessionStorageKeys.APP_IDS)
      .pipe(takeUntil(this._destroy$))
      .subscribe(appIds => {
        if (appIds?.tubularId) {
          this._tubularId = appIds.tubularId;
          this.loadData();
        }
      });
  }

  handleSignalR(data: any) {
    if (data.action === PeriforOnChangeMessages.REFRESH_RESULTS ||
        data.action === PeriforOnChangeMessages.REFRESH_PACKERS ||
        data.action === PeriforOnChangeMessages.REFRESH_STRING_INPUTS ||
        data.action === PeriforOnChangeMessages.STRESS_CALCULATIONS_FINISHED) {
      this.loadData();
    }
  }

  loadData() {
    this.isLoading.set(true);
    this.errorMessage.set(null);

    const sources = [
      this._packerService.getPackersForTubular(this._tubularId),
      this._loadCaseService.getLoadCasesSummary(true) // true = post-install load cases
    ];

    forkJoin(sources).pipe(
      map(([packersData, loadCases]) => {
        this.buildDropdownOptions(packersData as Packers, loadCases as LoadCaseSummaryResult[]);
        this.setDefaultSelections();
        this.updateExpansionJointFocusAvailability();

        // Load schematic if selections are valid
        if (this.selectedPackerIndex() !== undefined && this.selectedLoadCase() !== undefined) {
          this.loadSchematic();
        } else {
          this.isLoading.set(false);
        }
      }),
      catchError(err => {
        console.error('Error loading packer schematic viewer data:', err);
        this.errorMessage.set('Failed to load packers and load cases');
        this.isLoading.set(false);
        return [];
      })
    ).subscribe();
  }

  private buildDropdownOptions(packersData: Packers, loadCases: LoadCaseSummaryResult[]): void {
    // Build packer dropdown
    const packerOptions = packersData?.packers?.map((packer, index) => ({
      label: packer.name || `Packer ${index + 1}`,
      value: index
    })) || [];
    this.packerModels = packersData?.packers || [];
    this.packers.set(packerOptions);

    // Build load case dropdown
    const loadCaseOptions = loadCases?.map(lc => ({
      label: lc.name,
      value: lc.index
    })) || [];
    this.loadCases.set(loadCaseOptions);
  }

  private setDefaultSelections(): void {
    const packerOptions = this.packers();
    const loadCaseOptions = this.loadCases();

    // Set initial packer selection if not already set
    const currentPackerIndex = this.selectedPackerIndex();
    if ((currentPackerIndex === undefined || currentPackerIndex >= packerOptions.length) && packerOptions.length > 0) {
      this.selectedPackerIndex.set(packerOptions[0].value);
      this.store.update(state => ({ ...state, selectedPackerIndex: packerOptions[0].value }));
    }

    // Set initial load case selection if not already set
    if (this.selectedLoadCase() === undefined && loadCaseOptions.length > 0) {
      this.selectedLoadCase.set(loadCaseOptions[0].value);
      this.store.update(state => ({ ...state, selectedLoadCase: loadCaseOptions[0].value }));
    }

    // Initialize or migrate display mode - default to TubingToPackerForces
    const currentMode = this.selectedMode();
    if (!currentMode || currentMode === PackerSchematicDisplayMode.CrossSectionalAreas) {
      this.selectedMode.set(PackerSchematicDisplayMode.TubingToPackerForces);
      this.store.update(state => ({ ...state, selectedMode: PackerSchematicDisplayMode.TubingToPackerForces }));
    }

    // Initialize expansion joint focus if not set
    if (this.selectedExpansionJointFocus() === undefined) {
      this.selectedExpansionJointFocus.set(ExpansionJointFocus.ExpansionJoint);
      this.store.update(state => ({ ...state, selectedExpansionJointFocus: ExpansionJointFocus.ExpansionJoint }));
    }
  }

  async loadSchematic(): Promise<void> {
    const packerIndex = this.selectedPackerIndex();
    const loadCase = this.selectedLoadCase();
    if (packerIndex === undefined || loadCase === undefined) {
      this.errorMessage.set('Please select a packer and load case');
      this.svgContent.set('');
      return;
    }

    this.isLoading.set(true);
    this.errorMessage.set(null);

    try {
      // Get dimensions from component's host element minus toolbar height and margin
      const hostElement = this._elementRef.nativeElement;
      const containerWidth = Math.max((hostElement.offsetWidth || 600) - this.CONTAINER_MARGIN, this.MIN_DIMENSION);
      const containerHeight = Math.max((hostElement.offsetHeight || 600) - this.TOOLBAR_HEIGHT, this.MIN_DIMENSION);
      
      // Don't load if container is too small (still initializing)
      if (containerWidth < this.MIN_DIMENSION || containerHeight < this.MIN_DIMENSION) {
        this.isLoading.set(false);
        return;
      }
      
      // Cap width to prevent overly wide aspect ratios (packer schematics are vertical)
      const width = Math.min(containerWidth, this.MAX_SCHEMATIC_WIDTH);
      const height = containerHeight;

      // Build subtitle from load case name
      const selectedLoadCase = this.loadCases().find(lc => lc.value === loadCase);
      const subTitle = selectedLoadCase ? `Load Case: ${selectedLoadCase.label}` : '';

      const svg = await lastValueFrom(
        this._packerService.getPackerSchematic(
          packerIndex,
          this.selectedMode(),
          this._tubularId,
          subTitle,
          width,
          height,
          loadCase,
          this.selectedExpansionJointFocus()
        )
      );

      if (svg && svg.includes('<svg')) {
        this.svgContent.set(svg);
        // Effect in constructor will attach listeners when svgContent changes
      } else {
        this.errorMessage.set(svg || 'No schematic data available');
        this.svgContent.set('');
      }
    } catch (error: any) {
      console.error('Error loading packer schematic:', error);

      let errorMsg = 'Failed to load packer schematic';

      if (error?.error) {
        if (Array.isArray(error.error)) {
          errorMsg = error.error.join('\n');
        } else if (typeof error.error === 'string') {
          errorMsg = error.error;
        }
      } else if (error?.message) {
        errorMsg = error.message;
      }

      this.errorMessage.set(errorMsg.includes('No packers found')
        ? 'No packers available in this tubular string'
        : errorMsg);
      this.svgContent.set('');
    } finally {
      this.isLoading.set(false);
    }
  }

  onPackerChange(value: number): void {
    this.selectedPackerIndex.set(value);
    this.selectedExpansionJointFocus.set(ExpansionJointFocus.ExpansionJoint);
    this.store.update(state => ({ ...state, selectedPackerIndex: value, selectedExpansionJointFocus: ExpansionJointFocus.ExpansionJoint }));
    this.updateExpansionJointFocusAvailability();
    this.loadSchematic();
  }

  onLoadCaseChange(value: number): void {
    this.selectedLoadCase.set(value);
    this.store.update(state => ({ ...state, selectedLoadCase: value }));
    this.loadSchematic();
  }

  onModeChange(value: PackerSchematicDisplayMode): void {
    this.selectedMode.set(value);
    this.store.update(state => ({ ...state, selectedMode: value }));
    this.loadSchematic();
  }

  onExpansionJointFocusChange(value: ExpansionJointFocus): void {
    this.selectedExpansionJointFocus.set(value);
    this.store.update(state => ({ ...state, selectedExpansionJointFocus: value }));
    this.loadSchematic();
  }

  private updateExpansionJointFocusAvailability(): void {
    const packerIndex = this.selectedPackerIndex();
    if (packerIndex === undefined) {
      this.showExpansionJointFocusToggle.set(false);
      return;
    }
    const selectedPacker = this.packerModels[packerIndex];
    const shouldShowFocusToggle =
      selectedPacker?.hasExpansionJoint === true &&
      selectedPacker?.advancedExpansionJoint?.isDepthNotAtPacker === true;

    this.showExpansionJointFocusToggle.set(shouldShowFocusToggle);

    if (!shouldShowFocusToggle && this.selectedExpansionJointFocus() !== ExpansionJointFocus.ExpansionJoint) {
      this.selectedExpansionJointFocus.set(ExpansionJointFocus.ExpansionJoint);
      this.store.update(state => ({ ...state, selectedExpansionJointFocus: ExpansionJointFocus.ExpansionJoint }));
    }
  }

  private attachSvgEventListeners() {
    const container = this.schematicContainer?.nativeElement;
    if (!container) return;

    this.svgElement = container.querySelector('svg') as SVGSVGElement;
    if (!this.svgElement) return;

    // Force SVG to center content and scale uniformly
    this.svgElement.setAttribute('preserveAspectRatio', 'xMidYMid meet');

    // Store original viewBox for reset
    const viewBoxAttr = this.svgElement.getAttribute('viewBox');
    if (viewBoxAttr) {
      const [x, y, w, h] = viewBoxAttr.split(' ').map(Number);
      this.viewBox = { x, y, width: w, height: h };
      this.originalViewBox = { x, y, width: w, height: h };
      this.scale = 1;
      
      // Auto-fit content to remove excess whitespace
      const fitViewBox = this.computeContentFittedViewBox();
      if (fitViewBox) {
        this.viewBox = fitViewBox;
      }
      this.updateViewBox();
    }
  }

  private computeContentFittedViewBox(): ViewBoxHelpers.ViewBox | null {
    if (!this.svgElement) return null;
    try {
      const bbox = this.svgElement.getBBox();
      return ViewBoxHelpers.computeContentFittedViewBox(bbox, this.SVG_CONTENT_PADDING);
    } catch (e) {
      console.warn('Could not compute content bounding box:', e);
      return null;
    }
  }

  onMouseDown = (event: MouseEvent) => {
    this.isPanning.set(true);
    this.panStart = { x: event.clientX, y: event.clientY };
  }

  onMouseUp = () => {
    this.isPanning.set(false);
  }

  onMouseMove(event: MouseEvent) {
    if (this.isPanning() && this.svgElement) {
      const currentPosition: ViewBoxHelpers.Point = { x: event.clientX, y: event.clientY };
      this.viewBox = ViewBoxHelpers.computePanViewBox(
        this.viewBox,
        this.panStart,
        currentPosition,
        this.scale
      );
      this.updateViewBox();
      this.panStart = currentPosition;
      event.preventDefault();
    }
  }

  onMouseWheel(event: WheelEvent) {
    if (!this.svgElement) return;

    event.preventDefault();

    if (event.shiftKey) {
      // Pan with shift+scroll
      this.viewBox = ViewBoxHelpers.computeShiftPanViewBox(
        this.viewBox,
        event.deltaX,
        event.deltaY,
        this.scale
      );
      this.updateViewBox();
    } else {
      // Zoom around mouse cursor
      const mousePosition: ViewBoxHelpers.Point = { x: event.clientX, y: event.clientY };
      const rect = this.svgElement.getBoundingClientRect();
      
      this.viewBox = ViewBoxHelpers.computeZoomViewBox(
        this.viewBox,
        mousePosition,
        rect,
        event.deltaY
      );
      this.scale = ViewBoxHelpers.computeNewScale(this.scale, event.deltaY);
      this.updateViewBox();
    }
  }

  public onExportPng(): void {
    if (!this.svgElement) return;
    const selectedPackerName = this.packers().find(p => p.value === this.selectedPackerIndex())?.label || 'packer';
    PackerSchematicHelpers.downloadSvgAsPng(this.svgElement, `${selectedPackerName}_schematic.png`);
  }

  resetView() {
    if (!this.svgElement || !this.originalViewBox) return;

    // Prefer centering to live SVG content bounds
    const fitViewBox = this.computeContentFittedViewBox();
    this.viewBox = fitViewBox ?? { ...this.originalViewBox };
    this.scale = 1;
    this.updateViewBox();
  }

  private updateViewBox() {
    if (this.svgElement) {
      this.svgElement.setAttribute('viewBox', ViewBoxHelpers.formatViewBoxString(this.viewBox));
    }
  }

  public onPackerViewerContextMenu(event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.packerViewerCm?.show(event);
  }

  public async copySchematicAsImage(): Promise<void> {
    const result = await copySchematicToClipboard(this.svgElement);
    if (result.success) {
      this._toaster.showSuccess(result.message);
    } else {
      this._toaster.showError(result.message);
    }
  }

  ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }
}
